import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  DevTokenAuthProvider,
  FeatureFlagsAuthApiService,
  IAuthProvider,
  IAuthSession,
  IFeatureFlagsPermissions,
  OidcAuthProvider,
  RuntimeConfigService,
} from '@redngapps/feature-flags/data-access';
import { environment } from '@redngapps/feature-flags/env/types';
import { EEnvironment } from '@redngapps/shared/env/types';
import { Nullable } from '@redngapps/shared/types';
import { isNil } from 'lodash-es';
import { Observable, take } from 'rxjs';

import { selectFeatureFlagsLoggedIn } from '../+state/feature-flags-auth.selectors';

/**
 * Orchestrates authentication for the route guards and effects, delegating to the active
 * {@link IAuthProvider}. A deployed instance never falls back to dev auth: a missing/misconfigured
 * runtime config keeps users logged out rather than granting an unauthenticated session. Authentication
 * only proves identity — {@link getPermissions} resolves the read/write rights that gate the UI.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly store = inject(Store);
  private readonly runtimeConfigService = inject(RuntimeConfigService);
  private readonly oidcAuthProvider = inject(OidcAuthProvider);
  private readonly devTokenAuthProvider = inject(DevTokenAuthProvider);
  private readonly authApi = inject(FeatureFlagsAuthApiService);

  login(): Observable<Nullable<IAuthSession>> {
    return this.activeProvider.login();
  }

  logout(): Observable<void> {
    return this.activeProvider.logout();
  }

  restoreSession(): Observable<Nullable<IAuthSession>> {
    return this.activeProvider.restoreSession();
  }

  /**
   * Reads the user's per-instance read/write permissions. Doubles as the authorization gate: a
   * backend `403` (or other error) on a valid token means the user may not use the tool.
   */
  getPermissions(accessToken: string): Observable<IFeatureFlagsPermissions> {
    return this.authApi.getPermissions(accessToken);
  }

  isLoggedIn(): Observable<boolean> {
    return this.store.select(selectFeatureFlagsLoggedIn).pipe(take(1));
  }

  private get activeProvider(): IAuthProvider {
    const hasOidcClient = !isNil(this.runtimeConfigService.getConfig()?.googleClientId);
    const isLocalDevelopment = environment.name === EEnvironment.development;
    return isLocalDevelopment && !hasOidcClient ? this.devTokenAuthProvider : this.oidcAuthProvider;
  }
}
